import { Dayjs } from 'dayjs';
import { getLocale } from 'translations/locale';

import { MAX_DECIMAL_DIGITS } from 'business/constants';
import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import {
  useDashboardDailyAdvancementV1ControllerDailyLengthAdvancement,
  useDashboardDailyAdvancementV1ControllerRingPerShift,
} from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { SIMPLE_DATE_FORMAT } from 'technical/string/formatter';

const metersToFeet = (meters: number): number => {
  const METER_TO_FEET_FACTOR = 3.28084;
  return meters * METER_TO_FEET_FACTOR;
};

const formatDistance = (
  value: number,
  unit: 'meter' | 'foot',
  maxFractionDigits = MAX_DECIMAL_DIGITS,
): string => {
  return new Intl.NumberFormat(getLocale(), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
};

const formatMetricAdvancement = (
  distanceUnit: string,
  metricAdvancement: number,
): string => {
  if (!metricAdvancement) {
    return '0';
  }
  if (distanceUnit === 'm') {
    return formatDistance(metricAdvancement, 'meter');
  }

  return formatDistance(metersToFeet(metricAdvancement), 'foot');
};

export const useDailyAdvancement = ({
  currentTimestamp,
  constructionSiteId,
  distanceUnit,
}: {
  currentTimestamp: Dayjs;
  constructionSiteId: string;
  distanceUnit: string;
}) => {
  const restHeaders = useRestApiHeaders(Module_Enum.Dashboard);

  const {
    data: dailyLengthAdvancement,
    isFetching: isFetchingDailyLengthAdvancement,
    error: dailyLengthAdvancementError,
  } = useDashboardDailyAdvancementV1ControllerDailyLengthAdvancement({
    queryParams: { constructionSiteId, date: currentTimestamp.toISOString() },
    headers: restHeaders,
  });

  const {
    data: ringsPerShift,
    isFetching: isFetchingDailyRingPerShift,
    error: ringsPerShiftError,
  } = useDashboardDailyAdvancementV1ControllerRingPerShift({
    queryParams: {
      startDate: currentTimestamp.startOf('day').format(SIMPLE_DATE_FORMAT),
      endDate: currentTimestamp.endOf('day').format(SIMPLE_DATE_FORMAT),
      constructionSiteId,
    },
    headers: restHeaders,
  });

  const loading =
    isFetchingDailyRingPerShift || isFetchingDailyLengthAdvancement;

  const error =
    ringsPerShiftError?.payload || dailyLengthAdvancementError?.payload;

  const metricAdvancementInMeters =
    dailyLengthAdvancement?.dailyExcavatedLength ?? 0;

  const metricAdvancement = formatMetricAdvancement(
    distanceUnit,
    metricAdvancementInMeters,
  );

  const dailyAdvancement = {
    shift1Count: ringsPerShift?.shift1Count ?? 0,
    shift2Count: ringsPerShift?.shift2Count ?? 0,
    shift3Count: ringsPerShift?.shift3Count ?? 0,
    shift1Defined: ringsPerShift?.shift1Defined ?? false,
    shift2Defined: ringsPerShift?.shift2Defined ?? false,
    shift3Defined: ringsPerShift?.shift3Defined ?? false,
    metricAdvancement,
  };

  return { error, loading, dailyAdvancement };
};
